// The part of Material UI's usePagination that Pagination renders: the first
// and last page, the current page with one sibling on each side, an ellipsis
// for each gap, and the previous and next buttons. Pages are 1-based.
type PaginationItemType =
  'page' | 'previous' | 'next' | 'start-ellipsis' | 'end-ellipsis'

export interface PaginationItem {
  type: PaginationItemType
  page: number | null
  selected: boolean
  disabled: boolean
  onClick: () => void
}

interface UsePaginationOptions {
  count: number
  page: number
  onChange: (page: number) => void
}

const BOUNDARY_COUNT = 1
const SIBLING_COUNT = 1

const range = (start: number, end: number) =>
  Array.from({ length: Math.max(end - start + 1, 0) }, (_, i) => start + i)

const getItemList = (count: number, page: number) => {
  const startPages = range(1, Math.min(BOUNDARY_COUNT, count))
  const endPages = range(
    Math.max(count - BOUNDARY_COUNT + 1, BOUNDARY_COUNT + 1),
    count,
  )
  const siblingsStart = Math.max(
    Math.min(
      page - SIBLING_COUNT,
      count - BOUNDARY_COUNT - SIBLING_COUNT * 2 - 1,
    ),
    BOUNDARY_COUNT + 2,
  )
  const siblingsEnd = Math.min(
    Math.max(page + SIBLING_COUNT, BOUNDARY_COUNT + SIBLING_COUNT * 2 + 2),
    // endPages is empty when count is 1
    (endPages[0] ?? count + 1) - 2,
  )

  // a gap of one page shows that page instead of an ellipsis
  const startGap =
    siblingsStart > BOUNDARY_COUNT + 2
      ? ['start-ellipsis' as const]
      : BOUNDARY_COUNT + 1 < count - BOUNDARY_COUNT
        ? [BOUNDARY_COUNT + 1]
        : []
  const endGap =
    siblingsEnd < count - BOUNDARY_COUNT - 1
      ? ['end-ellipsis' as const]
      : count - BOUNDARY_COUNT > BOUNDARY_COUNT
        ? [count - BOUNDARY_COUNT]
        : []

  return [
    'previous' as const,
    ...startPages,
    ...startGap,
    ...range(siblingsStart, siblingsEnd),
    ...endGap,
    ...endPages,
    'next' as const,
  ]
}

export const usePagination = ({
  count,
  page,
  onChange,
}: UsePaginationOptions) => {
  const items: PaginationItem[] = getItemList(count, page).map(item => {
    if (typeof item === 'number') {
      return {
        type: 'page',
        page: item,
        selected: item === page,
        disabled: false,
        onClick: () => onChange(item),
      }
    }
    if (item === 'previous' || item === 'next') {
      const target = item === 'previous' ? page - 1 : page + 1
      return {
        type: item,
        page: target,
        selected: false,
        disabled: item === 'previous' ? page <= 1 : page >= count,
        onClick: () => onChange(target),
      }
    }
    return {
      type: item,
      page: null,
      selected: false,
      disabled: false,
      onClick: () => {},
    }
  })

  return { items }
}
